import { create } from 'zustand';

export type FocusZoneId = 'sidebar' | 'editor' | 'outline' | 'tools';

export interface FocusZone {
  id: FocusZoneId;
  label: string;
  order: number;
  isVisible(): boolean;
  focus(): void;
}

interface FocusRegistryState {
  zones: ReadonlyMap<FocusZoneId, FocusZone>;
  currentZoneId: FocusZoneId | null;
  register(zone: FocusZone): () => void;
  focusById(id: FocusZoneId): boolean;
  cycle(direction: 1 | -1): boolean;
  setCurrent(id: FocusZoneId | null): void;
}

export const useFocusRegistry = create<FocusRegistryState>((set, get) => ({
  zones: new Map(),
  currentZoneId: null,
  register(zone) {
    const next = new Map(get().zones);
    next.set(zone.id, zone);
    set({ zones: next });
    return () => {
      const m = new Map(get().zones);
      m.delete(zone.id);
      set({
        zones: m,
        currentZoneId: get().currentZoneId === zone.id ? null : get().currentZoneId,
      });
    };
  },
  focusById(id) {
    const zone = get().zones.get(id);
    if (!zone || !zone.isVisible()) return false;
    zone.focus();
    set({ currentZoneId: id });
    return true;
  },
  cycle(direction) {
    const visible = Array.from(get().zones.values())
      .filter((z) => z.isVisible())
      .sort((a, b) => a.order - b.order);
    if (visible.length === 0) return false;
    const currentIdx = visible.findIndex((z) => z.id === get().currentZoneId);
    const idx = (currentIdx + direction + visible.length * 2) % visible.length;
    const target = visible[currentIdx < 0 ? 0 : idx];
    target.focus();
    set({ currentZoneId: target.id });
    return true;
  },
  setCurrent(id) {
    set({ currentZoneId: id });
  },
}));

/** Test-only: clear the registry. */
export function _resetFocusRegistryForTest(): void {
  useFocusRegistry.setState({ zones: new Map(), currentZoneId: null });
}
